import { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// Curated list of icons appropriate for groups and sites
const SUGGESTED_ICONS = [
  'Building',
  'Building2',
  'MapPin',
  'Folder',
  'FolderOpen',
  'Users',
  'User',
  'Briefcase',
  'BriefcaseBusiness',
  'Film',
  'Video',
  'Camera',
  'Music',
  'Mic',
  'Calendar',
  'CalendarDays',
  'Clock',
  'Home',
  'Warehouse',
  'Factory',
  'Truck',
  'Car',
  'Plane',
  'Ship',
  'Globe',
  'Map',
  'Compass',
  'Navigation',
  'Target',
  'Flag',
  'Star',
  'Heart',
  'Zap',
  'Lightning',
  'Sun',
  'Moon',
  'Cloud',
  'TreePine',
  'Mountain',
  'Waves',
  'Droplet',
  'Flame',
  'Shield',
  'Lock',
  'Key',
  'Settings',
  'Cog',
  'Wrench',
  'Tool',
  'Hammer',
  'Package',
  'Box',
  'Archive',
  'FileText',
  'File',
  'FolderTree',
  'Layers',
  'Grid',
  'Layout',
  'LayoutGrid',
  'List',
  'Menu',
  'MoreHorizontal',
  'MoreVertical',
  'Circle',
  'Square',
  'Hexagon',
  'Triangle',
  'Diamond',
  'Dot',
] as const;

// Get all available lucide icons
const ALL_ICONS = Object.keys(LucideIcons).filter(
  (name) =>
    typeof (LucideIcons as unknown as Record<string, unknown>)[name] === 'function' &&
    name[0] === name[0].toUpperCase() &&
    !name.startsWith('Icon') &&
    name !== 'createLucideIcon'
) as string[];

interface IconPickerProps {
  value?: string | null;
  onChange: (icon: string | null) => void;
  label?: string;
  className?: string;
}

export function IconPicker({ value, onChange, label = 'Icon', className }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter icons based on search
  const filteredIcons = searchQuery
    ? ALL_ICONS.filter((icon) =>
        icon.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : SUGGESTED_ICONS;

  // Get the icon component
  const SelectedIcon = value ? (LucideIcons as unknown as Record<string, LucideIcon>)[value] : null;

  const handleSelectIcon = (iconName: string) => {
    if (value === iconName) {
      // Deselect if clicking the same icon
      onChange(null);
    } else {
      onChange(iconName);
    }
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start bg-background border-border hover:bg-card"
          >
            {SelectedIcon ? (
              <>
                <SelectedIcon className="w-4 h-4 mr-2" />
                <span className="flex-1 text-left">{value}</span>
                <X
                  className="w-4 h-4 ml-2 text-muted-foreground hover:text-foreground"
                  onClick={handleClear}
                />
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2 text-muted-foreground" />
                <span className="text-muted-foreground">Select an icon...</span>
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search icons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 bg-background border-border"
              />
            </div>
          </div>
          <ScrollArea className="h-64">
            <div className="p-2">
              <div className="grid grid-cols-6 gap-2">
                {filteredIcons.map((iconName) => {
                  const IconComponent = (LucideIcons as unknown as Record<string, LucideIcon>)[iconName];
                  if (!IconComponent) return null;

                  const isSelected = value === iconName;

                  return (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => handleSelectIcon(iconName)}
                      className={cn(
                        'p-2 rounded-lg border transition-colors',
                        isSelected
                          ? 'border-[#8FB560] bg-[#8FB560]/10'
                          : 'border-border hover:border-border hover:bg-card',
                        'flex items-center justify-center'
                      )}
                      title={iconName}
                    >
                      <IconComponent className="w-5 h-5 text-foreground" />
                    </button>
                  );
                })}
              </div>
              {filteredIcons.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No icons found matching "{searchQuery}"
                </div>
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
}

